<template>
  <section
    v-if="blok && blok.slides && blok.slides.length && !blok.hide"
    v-editable="blok"
    class="relative h-full"
    :class="marginClasses"
  >
    <SafelyHydrate>
      <div class="relative h-full">
        <!-- Navigation Arrows -->
        <template v-if="blok.slides.length > 1">
          <FadeInTransition>
            <BasicSliderArrow side="left" @click="swiper?.slidePrev()" />
          </FadeInTransition>
          <FadeInTransition>
            <BasicSliderArrow side="right" @click="swiper?.slideNext()" />
          </FadeInTransition>
        </template>

        <Swiper
          v-if="blok.slides.length > 1"
          ref="swiperRef"
          :modules="[SwiperPagination, SwiperAutoplay]"
          :pagination="{
            enabled: true,
            clickable: true,
          }"
          :autoplay="{
            delay: 5000,
            disableOnInteraction: false,
          }"
          :loop="true"
          :speed="700"
          :grab-cursor="true"
          class="h-full"
          @swiper="onSwiper"
        >
          <SwiperSlide v-for="(slide, index) in blok.slides" :key="index">
            <Intersect
              :threshold="0.5"
              @enter.once="handleVisibility(slide, index)"
            >
              <StageSlide
                :index="index"
                :slide="slide"
                :ratio="aspectRatio"
                :style="aspectRatio && { aspectRatio }"
                :has-multiple-slides="blok.slides.length > 1"
                class="min-h-[550px] sm:min-h-0"
              />
            </Intersect>
          </SwiperSlide>
        </Swiper>

        <!-- Single slide without Swiper -->
        <div v-else-if="firstSlide">
          <Intersect
            :threshold="0.5"
            @enter.once="handleVisibility(firstSlide, 0)"
          >
            <StageSlide
              :index="0"
              :slide="firstSlide"
              :ratio="aspectRatio"
              :style="aspectRatio && { aspectRatio }"
              :has-multiple-slides="false"
              class="min-h-[550px] sm:min-h-0"
            />
          </Intersect>
        </div>
      </div>
    </SafelyHydrate>
  </section>
</template>

<script setup lang="ts">
import { provide, computed, type PropType, ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import {
  Pagination as SwiperPagination,
  Autoplay as SwiperAutoplay,
} from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper/types'
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { useDeviceType } from '~/composables/useDeviceType'
import 'swiper/css'
import 'swiper/css/pagination'
import type {
  SbStage,
  SbStageSlide,
} from '~/cms/storyblok/types/storyblok.d.ts'
import { CTA_PROMOTION_CONTEXT_KEY } from '#layers/base/constants/injectionKeys'

const props = defineProps({
  blok: {
    type: Object as PropType<SbStage>,
    required: true,
  },
})

const { isMobile } = useDeviceType()
const { trackPromotionSelect, trackPromotionView } = useTrackingEvents()
const { marginClasses } = useStoryblokMargins(props.blok)

const swiper = ref<SwiperType>()
const isInitialized = ref(false)

// StageSlide mounts the slide's `cta_button` blok as a real CtaButton, and this
// block already emits both promotion events for every slide, so the CTA must not
// emit competing ones (ECOM-2766).
provide(CTA_PROMOTION_CONTEXT_KEY, { claimedBy: 'heroslider' })

// emit does not work, so we use provide/inject
provide('stage', {
  ctaClicked: (slide: SbStageSlide, index: number): void => {
    trackPromotionSelect({
      id: props.blok.promotion_id || 'stage_promotion_id',
      name: props.blok.promotion_name || 'stage_promotion_name',
      promoType: 'heroslider',
      position: Number(index) + 1,
      creativeNameSb: slide.creative_name || '',
    })
  },
})

const aspectRatio = computed(() => {
  if (isMobile.value) {
    return ''
  }
  return props.blok.ratio?.replace(':', '/') || '16/9'
})

const firstSlide = computed(() => props.blok.slides?.[0])

const handleVisibility = (slide: SbStageSlide, index: number) => {
  trackPromotionView({
    id: props.blok.promotion_id || 'stage_promotion_id',
    name: props.blok.promotion_name || 'stage_promotion_name',
    promoType: 'heroslider',
    position: Number(index) + 1,
    creativeNameSb: slide.creative_name || '',
  })
}

const onSwiper = (swiperInstance: SwiperType) => {
  swiper.value = swiperInstance
  // Small delay to ensure smooth transition
  setTimeout(() => {
    isInitialized.value = true
  }, 50)
}
</script>

<style lang="scss" scoped>
:deep(.swiper-pagination) {
  bottom: 40px !important;
}

:deep(.swiper-pagination-bullet) {
  width: 8px;
  height: 8px;
  background: white;
  opacity: 0.5;
}

:deep(.swiper-pagination-bullet-active) {
  opacity: 1;
}
</style>
